import qs from 'qs'
import { ReactElement, useEffect } from 'react'

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig,
} from 'axios'
import { AccountInfo } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { useAppDispatch, useAppSelector } from 'app/services/hooks/store-hooks'
import { useGetToken } from 'app/services/hooks/useGetToken'
import {
  getMaintenanceMessage,
  isMaintenanceModeError,
} from 'app/services/utils/maintenanceMode'
import {
  activateMaintenance,
  recoverFromMaintenance,
} from 'app/store/slice/maintenanceSlice'

const apiUrl = process.env.REACT_APP_API_URL

const options: CreateAxiosDefaults = {
  baseURL: apiUrl,
  headers: {
    'Access-Control-Allow-Origin': '*',
    withCredentials: true,
  },
  paramsSerializer: {
    serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
  },
}

const axiosInstance: AxiosInstance = axios.create(options)

export const formatAuthorizationHeader = (
  accessToken: string | null | undefined
) => {
  if (!accessToken) return undefined
  return `Bearer ${accessToken.replace(/^Bearer\s+/i, '')}`
}

const AxiosInterceptor = ({ children }: { children: ReactElement }) => {
  const getToken = useGetToken()
  const dispatch = useAppDispatch()
  const isMaintenanceActive = useAppSelector(
    (state) => state.maintenance.isActive
  )
  const { instance: msalInstance } = useMsal()
  const currentAccount = msalInstance.getActiveAccount() as
    | AccountInfo
    | undefined

  useEffect(() => {
    const onRequest = async (config: InternalAxiosRequestConfig) => {
      const accessToken = await getToken()
      config.headers = config.headers ?? {}
      const authorization = formatAuthorizationHeader(accessToken)
      if (authorization) {
        config.headers.Authorization = authorization
      }
      return config
    }

    const onRequestError = (error: AxiosError): Promise<AxiosError> => {
      const message =
        (error.response && error.response.data) || 'Something went wrong!'
      return Promise.reject(message)
    }

    const onResponse = (response: AxiosResponse): AxiosResponse => {
      if (isMaintenanceActive) {
        dispatch(recoverFromMaintenance())
      }
      return response
    }

    const onResponseError = async (error: AxiosError): Promise<AxiosError> => {
      const originalRequest = error.config as
        | (InternalAxiosRequestConfig & { retryAttempted?: boolean })
        | undefined

      if (isMaintenanceModeError(error)) {
        dispatch(
          activateMaintenance({
            message: getMaintenanceMessage(error),
          })
        )
      }

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest.retryAttempted
      ) {
        const token = await getToken()
        const nextAuthorization = formatAuthorizationHeader(token)
        const currentAuthorization = originalRequest.headers?.Authorization

        if (nextAuthorization && currentAuthorization !== nextAuthorization) {
          originalRequest.retryAttempted = true
          originalRequest.headers = originalRequest.headers ?? {}
          originalRequest.headers.Authorization = nextAuthorization
          return axiosInstance(originalRequest)
        }
      }
      return Promise.reject(
        (error.response && error.response.data) || 'Something went wrong!'
      )
    }
    const requestInterceptor = axiosInstance.interceptors.request.use(
      onRequest,
      onRequestError
    )
    const responseInterceptor = axiosInstance.interceptors.response.use(
      onResponse,
      onResponseError
    )

    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor)
      axiosInstance.interceptors.response.eject(responseInterceptor)
    }
  }, [currentAccount, dispatch, getToken, isMaintenanceActive, msalInstance])

  return children
}

export default axiosInstance
export { AxiosInterceptor }
